import clsx from "clsx";
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from "react-hook-form";

import { AssetsPageContent } from '@cms/pages/app/AssetsPage';
import { BROWSER_EVENTS } from "@cms/utils/constants";
import { getAssetType, ISystemAsset, TAssetMetadata } from "@dl/declarations";
import { ImageAsset } from './Asset';
import { ControlledSystemDialog } from './Dialog';
import { CancelButton } from "./shared";

import { LuUpload } from "react-icons/lu";
import { MdOutlineImage } from "react-icons/md";

const SIZES = {
    full: "w-full h-full",
}

type WithSrcUrl = {
    file?: never
    srcUrl: string
}

type WithImageFile = {
    file: File | null
    srcUrl?: never
}

type WithChangeHandler = {
    handleChange: (files: FileList | null) => void;
    handleClick?: never;
}

type WithClickHandler = {
    handleClick: () => void;
    handleChange?: never;
}

type CommonProps = {
    className?: string;
    showBorder?: boolean;
    showCorners?: boolean;
    showFullPreview?: boolean;
    allowMultiple?: boolean;
    size?: keyof typeof SIZES;

    mainAction?: {
        show: boolean;
        actionText?: string;
        actionIcon?: React.ReactNode;
        mainIcon?: React.ReactNode;
        dialogTitle?: string;
    }

    editAction?: {
        show: boolean;
        actionText?: string;
        actionIcon?: React.ReactNode;
        contentComponent?: React.ReactNode;
    }
}

type FileInputProps = Omit<CommonProps, "editAction"> & ((WithSrcUrl | WithImageFile) & (WithChangeHandler | WithClickHandler));

export const FileInput: React.FC<FileInputProps> = memo((props) => {
    const {
        className = "",
        size = 'full',
        showBorder = true,
        showCorners = true,
        showFullPreview = false,
        allowMultiple = false,

        mainAction,

        file,
        srcUrl,
        handleClick,
        handleChange,
    } = props;

    const renderFile = useCallback(() => {
        if (file) {
            const mimeType = getAssetType(file)

            switch (mimeType) {
                case 'image':
                    return (
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                backgroundImage: `url(${URL.createObjectURL(file)})`,
                                backgroundPosition: "center",
                                backgroundSize: "contain",
                                backgroundRepeat: showFullPreview ? "no-repeat" : "cover"
                            }}
                        />
                    )

                case "video":
                    return (
                        <video
                            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                            src={URL.createObjectURL(file)}
                            controls
                        />
                    )

                default:
                    return null
            }
        }

        if (srcUrl) {
            const mimeType = getAssetType(srcUrl)

            switch (mimeType) {
                case 'image':
                    return (
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                backgroundImage: `url(${new URL(srcUrl)})`,
                                backgroundSize: "contain",
                                backgroundPosition: "center",
                                backgroundRepeat: showFullPreview ? "no-repeat" : "cover"
                            }}
                        />
                    )

                case "video":
                    return (
                        <video
                            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                            src={srcUrl}
                            controls
                        />
                    )

                default:
                    return null
            }
        }

        return null
    }, [file, srcUrl, showFullPreview])

    const hasFile = useMemo(() => {
        return file || srcUrl
    }, [file, srcUrl])

    const [isDragging, setIsDragging] = useState(false)

    const preventDefault = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }, [])

    const handleDragEnter = useCallback(
        (e: React.DragEvent) => {
            preventDefault(e)
            if (!e.dataTransfer.types.includes('Files')) return
            setIsDragging(true)
        },
        [preventDefault]
    )

    const handleDragLeave = useCallback(
        (e: React.DragEvent) => {
            preventDefault(e)
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setIsDragging(false)
            }
        },
        [preventDefault]
    )

    const handleDragOver = useCallback(
        (e: React.DragEvent) => {
            preventDefault(e)
            if (e.dataTransfer.types.includes('Files')) {
                e.dataTransfer.dropEffect = 'copy'
            }
        },
        [preventDefault]
    )

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            preventDefault(e)
            setIsDragging(false)
            if (!handleChange) return
            const { files } = e.dataTransfer
            if (!files?.length) return
            if (allowMultiple) {
                handleChange(files)
            } else {
                const dt = new DataTransfer()
                dt.items.add(files[0])
                handleChange(dt.files)
            }
        },
        [handleChange, allowMultiple, preventDefault]
    )

    const shouldShowMainAction = mainAction?.show

    return (
        <div
            className={clsx(
                "group text-cs-700 cursor-pointer relative aspect-2/1",
                showBorder && "border border-cs-700 overflow-hidden",
                showCorners && "rounded-xl",
                hasFile ? "cs-file-input-placeholder-bg" : "bg-white",
                isDragging && "ring-2 ring-cs-500 ring-offset-2",
                SIZES[size],
                className
            )}
            onClick={handleClick}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <div
                className={clsx(
                    "z-10 absolute flex flex-col justify-center items-center gap-4 pointer-events-none",
                    hasFile ?
                        "top-4 left-4"
                        :
                        "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                )}
            >
                {shouldShowMainAction && (
                    <>
                        {(!hasFile) && (
                            mainAction?.mainIcon || <MdOutlineImage className="w-8 h-8" />
                        )}

                        <div
                            className={clsx(
                                "w-max flex items-center gap-4 border py-1 px-2 rounded transition-all duration-300",
                                hasFile ? "bg-cs-200 opacity-0 group-hover:opacity-100" : "bg-transparent opacity-100"
                            )}
                        >
                            <span
                                className="text-sm font-semibold"
                                dangerouslySetInnerHTML={{ __html: mainAction?.actionText || "Upload file" }}
                            >
                            </span>

                            {mainAction?.actionIcon || <LuUpload />}
                        </div>
                    </>
                )}
            </div>

            {(shouldShowMainAction && handleChange) && (
                <>
                    {hasFile && (
                        <CancelButton
                            className="text-white absolute top-2 right-2 z-10"
                            onClick={() => handleChange(null)}
                        />
                    )}

                    <input
                        name=""
                        type="file"
                        accept="image/*"
                        className="h-full w-full opacity-0 cursor-pointer"
                        onChange={(e) => {
                            handleChange(e.target.files);
                        }}
                        multiple={allowMultiple}
                    />
                </>
            )}

            {renderFile()}
        </div>
    )
})

type AssetInputProps = CommonProps & {
    asset: TAssetMetadata | null;
    handleChange: (asset: TAssetMetadata) => void;
    hasError?: boolean;
}

export const AssetInput: React.FC<AssetInputProps> = (props) => {
    const {
        className = "",
        size = 'full',
        hasError = false,
        showBorder = true,
        showCorners = true,
        showFullPreview = false,

        mainAction,
        editAction,

        asset,
        handleChange,
    } = props;

    const renderItem = useCallback((row: ISystemAsset) => {
        switch (row.mime_type) {
            case 'image':
                return (
                    <ImageAsset
                        asset={row}
                        actions={{
                            useImage: {
                                show: true,
                                action(asset) {
                                    handleChange(asset);
                                },
                            },
                            editImage: {
                                show: true,
                            },
                        }}
                    />
                )

            case 'video':
                return null

            default:
                return null
        }
    }, [])

    const renderFile = useCallback(() => {
        if (asset) {
            const mimeType = getAssetType(asset.src_url)

            switch (mimeType) {
                case 'image':
                    return (
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                backgroundImage: `url(${new URL(asset.src_url)})`,
                                backgroundPosition: "center",
                                backgroundSize: showFullPreview ? "contain" : "cover",
                                backgroundRepeat: showFullPreview ? "no-repeat" : "cover"
                            }}
                        />
                    )

                case "video":
                    return (
                        <video
                            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                            src={asset.src_url}
                            controls
                        />
                    )

                default:
                    return null
            }
        }

        return null
    }, [asset, showFullPreview])

    const hasFile = useMemo(() => {
        return Boolean(asset)
    }, [asset])

    return (
        <div
            className={clsx(
                "group text-cs-700 relative aspect-2/1",
                showBorder && "border border-cs-700 overflow-hidden",
                hasError && "border-red-500",
                hasFile ? "cs-file-input-placeholder-bg" : "bg-white",
                showCorners && "rounded-xl",
                SIZES[size],
                className
            )}
        >
            {mainAction?.show && (
                <ControlledSystemDialog
                    dialogProps={{
                        title: mainAction.dialogTitle || 'Select asset'
                    }}
                    trigger={{
                        component: (
                            <div
                                role="button"
                                className={clsx(
                                    "z-10 absolute cursor-pointer flex flex-col justify-center items-center gap-4",
                                    hasFile ?
                                        "top-4 left-4"
                                        :
                                        "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                                )}
                            >
                                {(!hasFile) && (
                                    mainAction?.mainIcon || <MdOutlineImage className="w-8 h-8" />
                                )}

                                <div
                                    className={clsx(
                                        "w-max flex items-center gap-4 border py-1 px-2 rounded transition-all duration-300",
                                        hasFile ? "bg-cs-200 opacity-0 group-hover:opacity-100" : "bg-transparent opacity-100"
                                    )}
                                >
                                    <span className="text-sm font-semibold">
                                        {mainAction?.actionText || "Upload file"}
                                    </span>

                                    {mainAction?.actionIcon || <LuUpload />}
                                </div>
                            </div>
                        )
                    }}
                    content={{
                        component: (
                            <AssetsPageContent renderItem={renderItem} />
                        )
                    }}
                    actionsProps={{
                        hide: true
                    }}
                />
            )}

            {(hasFile && editAction?.show) && (
                <ControlledSystemDialog
                    dialogProps={{
                        title: editAction.actionText || 'Edit asset',
                        maxWidth: 'sm',
                    }}
                    trigger={{
                        component: (
                            <div
                                role="button"
                                className={clsx(
                                    "z-10 absolute cursor-pointer flex flex-col justify-center items-center gap-4",
                                    hasFile ?
                                        "top-14 left-4"
                                        :
                                        "hidden"
                                )}
                            >
                                <div
                                    className={clsx(
                                        "w-max flex items-center gap-4 border py-1 px-2 rounded transition-all duration-300",
                                        hasFile ? "bg-cs-200 opacity-0 group-hover:opacity-100" : "bg-transparent opacity-100"
                                    )}
                                >
                                    <span className="text-sm font-semibold">
                                        {editAction.actionText || "Edit asset"}
                                    </span>

                                    {editAction?.actionIcon}
                                </div>
                            </div>
                        ),
                    }}
                    content={{
                        component: editAction.contentComponent || <AssetsPageContent renderItem={renderItem} />
                    }}
                    actionsProps={{
                        hide: true
                    }}
                />
            )}

            {renderFile()}
        </div>
    )
}

type ControlledAssetInputProps = Omit<AssetInputProps, "asset" | "handleChange"> & {
    name: string;
    valueKey?: "_id" | "src_url";
    initialValue: TAssetMetadata | null;
}

export const ControlledAssetInput: React.FC<ControlledAssetInputProps> = (props) => {
    const {
        name,
        initialValue,
        valueKey = "_id",

        ...restOfProps
    } = props;

    const { control, setValue, watch } = useFormContext();

    const {
        field: { onChange },
        formState
    } = useController({
        name,
        control,
    });

    // To handle the asset object that holds all the fields of the asset and used for the asset input
    const ASSET_OBJECT_FIELD_NAME = `${name}_object`;
    // To handle the asset info that holds the asset fields and used for edit asset metadata
    const ASSET_INFO_FIELD_NAME = `${name}_info`;

    const formAsset = watch(ASSET_OBJECT_FIELD_NAME);

    const displayAsset = formAsset !== undefined ? formAsset : initialValue ?? null;

    const handleChangeSelectedImage = useCallback(
        (asset: TAssetMetadata | null) => {
            dispatchEvent(new Event(BROWSER_EVENTS.CLOSE_MODAL));
            onChange(asset ? asset[valueKey] : "");
            setValue(ASSET_OBJECT_FIELD_NAME, asset, { shouldDirty: true });
            setValue(ASSET_INFO_FIELD_NAME, asset, { shouldDirty: true });
        },
        [onChange, valueKey, setValue]
    );

    useEffect(() => {
        if (!initialValue) return;

        const hasStoredAsset = Boolean(formAsset)
        if (hasStoredAsset) return;

        setValue(ASSET_OBJECT_FIELD_NAME, initialValue, { shouldDirty: false });
        onChange(initialValue[valueKey]);
    }, [initialValue, valueKey, setValue, onChange, formAsset]);

    const hasError = Boolean(formState?.errors?.[name]);

    return (
        <AssetInput
            asset={displayAsset}
            handleChange={handleChangeSelectedImage}
            showFullPreview
            hasError={hasError}
            {...restOfProps}
        />
    )
}